import styled from 'styled-components'
import React, { forwardRef, InputHTMLAttributes, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { requiredValidator, useValidation, Validator } from '../../hooks/useValidation'

export type TextInputProps = React.PropsWithRef<Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'defaultValue'> & {
  label?: string
  onChange?: (address: string) => void
  validators?: Validator[]
  defaultValue?: string
}>

export type TextInputHandle = { setValue(v: string): void }

const Wrapper = styled.div`
  padding: 4px;
`

const LabelWrapper = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Label = styled.span<{ isValid: boolean }>`
  font-size: 0.8rem;
  text-transform: uppercase;
  transition: color, font-weight .1s ease-in-out;
  text-indent: 1rem;

  ${({ isValid }) => `
    color: ${isValid ? 'inherit' : '#c51854' };
  `}
`

const Input = styled.input<InputHTMLAttributes<HTMLInputElement> & { isValid: boolean }>`
  height: 2.5rem;
  border: none;
  border-radius: 2.5rem;
  padding: 0.4rem 1rem;
  background: #7632b3;
  outline: none;
  transition: background .3s ease-in-out;
  color: #fff;
  background: #ff9671;

  &::placeholder {
    color: #fff;
    opacity: 0.65;
  }

  ${({ isValid }) => isValid ? `` : `
    &:focus {
      outline: 3px solid #db2564;
    }

    &:not(:focus) {
      outline: 3px solid #db2564;
    }
  `}
`

const ErrorList = styled.ul`
  margin: 0;
  padding: 0.5rem 2rem;
  font-size: 0.8rem;
  color: #eb258b;
  list-style: none;
`

export const TextInput = forwardRef<TextInputHandle, TextInputProps>(({
  label,
  validators = [],
  defaultValue = '',
  onChange,
  required,
  ...inputProps
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const finalValidators = useMemo(
    () => required ? [requiredValidator, ...validators] : validators,
    [required, validators]
  )

  const { isValid, errors } = useValidation({
    validators: finalValidators,
    fieldRef: inputRef
  })

  useImperativeHandle(ref, () => ({
    setValue(v: string) {
      if (inputRef.current) {
        inputRef.current.value = v
        setValue(v)
      }
    }
  }))

  useEffect(() => {
    if (value) {
      onChange?.(value)
    }
  }, [value, onChange])

  return (
    <Wrapper>
      <LabelWrapper>
        <Label isValid={isValid}>{label}</Label>
        <Input
          {...inputProps}
          ref={inputRef}
          type="text"
          isValid={isValid}
          onChange={({ target }) => setValue(target.value)}
          {...(defaultValue ? ({ defaultValue }) : {})}
          data-testid="InputField"
        />
      </LabelWrapper>
      {errors.length > 0 && (
        <ErrorList>
          {errors.map(msg => (
            <li key={msg}>{msg}</li>
          ))}
        </ErrorList>
      )}
    </Wrapper>
  )
})
